import { Link } from "react-router-dom";
import { ArrowUpRight, Wifi, UtensilsCrossed, Waves, Car, Plane, Sunset } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { HERO_IMAGE, SECONDARY_IMAGE, ROOMS, HOST } from "../lib/content";

const ICONS = [Waves, Sunset, UtensilsCrossed, Wifi, Car, Plane];

export default function Home() {
  return (
    <>
      <HeroSection />
      <IntroSection />
      <HostSection />
      <RoomsTeaserSection />
      <FeaturesSection />
      <ImageBandSection />
      <HomeCtaSection />
    </>
  );
}

function HeroSection() {
  const { t } = useI18n();

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="relative h-[92vh] min-h-[620px] w-full">
        <img
          src={HERO_IMAGE}
          alt="Plage de Grand-Popo"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-[#2C241B]/55" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 h-full flex flex-col justify-end pb-20 md:pb-28">
          <div className="overline text-[#F9F6F0]/90 fade-up">
            {t("hero.overline")}
          </div>
          <h1
            className="mt-5 text-white max-w-4xl text-5xl md:text-7xl lg:text-[88px] leading-[1.03] tracking-tight fade-up delay-100"
            data-testid="hero-title"
          >
            {t("hero.title")}
          </h1>
          <p className="mt-6 text-[#F9F6F0]/90 text-lg md:text-xl max-w-2xl fade-up delay-200">
            {t("hero.subtitle")}
          </p>

          <div className="mt-10 flex flex-wrap gap-4 fade-up delay-300">
            <Link to="/reserver" className="btn-primary" data-testid="hero-book-cta">
              {t("hero.cta_book")} <ArrowUpRight size={16} />
            </Link>
            <Link
              to="/chambres"
              className="btn-outline border-[#F9F6F0] text-[#F9F6F0] hover:bg-[#F9F6F0] hover:text-[#2C241B]"
              data-testid="hero-rooms-cta"
              style={{ borderColor: "#F9F6F0", color: "#F9F6F0" }}
            >
              {t("hero.cta_rooms")}
            </Link>
          </div>
        </div>

        <div className="absolute top-0 right-5 md:right-8 h-full hidden md:flex flex-col justify-end pb-10 text-[#F9F6F0]/80 text-[11px] tracking-[0.3em] uppercase">
          <div style={{ writingMode: "vertical-rl" }} className="rotate-180">
            06°16′N · 01°49′E — Atlantic coast
          </div>
        </div>
      </div>
    </section>
  );
}

function IntroSection() {
  const { t, lang } = useI18n();

  return (
    <section className="py-24 md:py-32" data-testid="intro-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
        <div className="md:col-span-5">
          <div className="overline">{t("intro.overline")}</div>
          <h2 className="mt-5 text-4xl md:text-5xl leading-[1.1]">
            {t("intro.title")}
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-lg leading-relaxed text-[#5C5042]">
            {t("intro.body")}
          </p>
          <div className="mt-8 border-t border-[#6B4A2B]/15 pt-8 grid grid-cols-3 gap-6">
            <Stat number="4" label={lang === "fr" ? "Chambres" : "Rooms"} />
            <Stat number="0m" label={lang === "fr" ? "De la plage" : "From the beach"} />
            <Stat number="2026" label={lang === "fr" ? "Tarifs" : "Rates"} />
          </div>
        </div>
      </div>
    </section>
  );
}

function HostSection() {
  const { t } = useI18n();

  return (
    <section className="py-20 md:py-28 bg-[#EFEAE1]" data-testid="host-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-12 gap-10 md:gap-16 items-center">
        <div className="md:col-span-5 img-zoom relative aspect-[4/5] w-full max-w-md mx-auto md:mx-0 bg-[#F9F6F0]">
          <img
            src={HOST.image}
            alt={`${t("host.title")}`}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
        <div className="md:col-span-7 md:pl-8">
          <div className="overline">{t("host.overline")}</div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.1]">
            {t("host.title")}
          </h2>
          <p className="mt-6 text-lg text-[#5C5042] leading-relaxed max-w-xl">
            {t("host.body")}
          </p>
          <div className="mt-8 font-serif-display italic text-2xl text-[#6B4A2B]">
            {t("host.signature")}
          </div>
        </div>
      </div>
    </section>
  );
}

function RoomsTeaserSection() {
  const { t, lang } = useI18n();

  return (
    <section className="py-8 md:py-16 bg-[#EFEAE1]" data-testid="rooms-teaser-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="overline">{t("rooms.overline")}</div>
            <h2 className="mt-4 text-4xl md:text-5xl max-w-2xl leading-[1.1]">
              {t("rooms.title")}
            </h2>
          </div>
          <Link
            to="/chambres"
            className="text-[#6B4A2B] text-sm tracking-[0.2em] uppercase hover:underline underline-offset-4 inline-flex items-center gap-2"
            data-testid="rooms-teaser-link"
          >
            {t("rooms.details")} <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROOMS.map((r, i) => (
            <Link
              key={r.id}
              to={`/chambres#${r.id}`}
              data-testid={`room-teaser-${r.id}`}
              className="group img-zoom relative block h-[440px] bg-black/10"
            >
              <img
                src={r.image}
                alt={lang === "fr" ? r.nameFr : r.nameEn}
                className="h-full w-full object-cover"
                loading={i > 1 ? "lazy" : "eager"}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <div className="overline text-white/80">
                  {r.priceTo ? `${r.priceFrom}–${r.priceTo}€` : `${r.priceFrom}€`}
                  <span className="ml-1 opacity-70 tracking-normal lowercase">
                    {t("rooms.per_night")}
                  </span>
                </div>
                <h3 className="mt-2 text-2xl">
                  {lang === "fr" ? r.nameFr : r.nameEn}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const { t, lang } = useI18n();
  const features = t("features.items");

  return (
    <section className="py-24 md:py-32" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-3xl">
          <div className="overline">{t("features.title")}</div>
          <h2 className="mt-4 text-4xl md:text-5xl leading-[1.1]">
            {lang === "fr"
              ? "L'essentiel, soigné."
              : "The essentials, done right."}
          </h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
          {features.map((f, i) => {
            const Icon = ICONS[i] || Waves;
            return (
              <div
                key={f.label}
                data-testid={`feature-${i}`}
                className="flex gap-5 border-t border-[#6B4A2B]/15 pt-6"
              >
                <div className="text-[#4F7942] shrink-0">
                  <Icon size={28} strokeWidth={1.2} />
                </div>
                <div>
                  <div className="font-serif-display text-xl text-[#2C241B]">
                    {f.label}
                  </div>
                  <p className="mt-2 text-sm text-[#5C5042] leading-relaxed">
                    {f.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ImageBandSection() {
  const { lang } = useI18n();

  return (
    <section className="relative" data-testid="image-band">
      <div className="relative h-[70vh] min-h-[460px] overflow-hidden">
        <img
          src={SECONDARY_IMAGE}
          alt="Coucher de soleil sur l'océan"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 md:px-8 flex items-center">
          <div className="max-w-2xl text-white">
            <div className="overline text-white/80">Grand-Popo</div>
            <h2 className="mt-4 text-4xl md:text-6xl leading-[1.05]">
              {lang === "fr"
                ? "Du sable, des cocotiers, un fleuve et l'Atlantique."
                : "Sand, coconut palms, a river, and the Atlantic."}
            </h2>
            <Link
              to="/grand-popo"
              data-testid="band-location-link"
              className="mt-8 inline-flex items-center gap-2 text-sm tracking-[0.22em] uppercase text-white border-b border-white/60 pb-1 hover:border-white"
            >
              {lang === "fr" ? "Découvrir la région" : "Discover the region"}
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeCtaSection() {
  const { t, lang } = useI18n();

  return (
    <section className="py-24 md:py-32" data-testid="home-cta-section">
      <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
        <div className="overline">{t("booking.overline")}</div>
        <h2 className="mt-5 text-4xl md:text-5xl leading-[1.1]">
          {lang === "fr"
            ? "Écrivez-nous, on s'occupe du reste."
            : "Write to us, we'll handle the rest."}
        </h2>
        <p className="mt-6 text-[#5C5042] text-lg">{t("booking.subtitle")}</p>
        <div className="mt-10 flex gap-4 justify-center flex-wrap">
          <Link to="/reserver" className="btn-primary" data-testid="home-cta-book">
            {t("hero.cta_book")}
          </Link>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-serif-display text-4xl text-[#6B4A2B]">{number}</div>
      <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-[#5C5042]">
        {label}
      </div>
    </div>
  );
}
